import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { LANGUAGES, RTL_LANGUAGES } from '../../i18n';

/* Inline SVG flags — no external CDN dependency */
function FlagIL({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={Math.round(size * 0.73)} viewBox="0 0 640 468" className="rounded-sm flex-shrink-0">
      <rect width="640" height="468" fill="#fff"/>
      <rect y="54" width="640" height="5" fill="#0038b8"/>
      <rect y="54" width="640" height="48" fill="#0038b8"/>
      <rect y="366" width="640" height="48" fill="#0038b8"/>
      <polygon points="320,139 355,216 290,176 350,176 285,216" fill="none" stroke="#0038b8" strokeWidth="24"/>
      <polygon points="320,329 285,252 350,292 290,292 355,252" fill="none" stroke="#0038b8" strokeWidth="24"/>
    </svg>
  );
}

function FlagUS({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={Math.round(size * 0.53)} viewBox="0 0 190 100" className="rounded-sm flex-shrink-0">
      <rect width="190" height="100" fill="#bf0a30"/>
      {[0,2,4,6,8,10,12].map(i => <rect key={i} y={i * 100/13} width="190" height={100/13} fill={i % 2 === 0 ? '#bf0a30' : '#fff'}/>)}
      <rect width="76" height={100*7/13} fill="#002868"/>
      {/* simplified stars */}
      {[0,1,2,3,4].map(r =>
        [0,1,2,3,4,5].slice(0, r % 2 === 0 ? 6 : 5).map((c, ci) => (
          <circle key={`${r}-${ci}`} cx={6 + (r % 2 === 0 ? 0 : 6.3) + ci * 12.7} cy={4.5 + r * 10.5} r="2.2" fill="#fff"/>
        ))
      )}
    </svg>
  );
}

function FlagRU({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={Math.round(size * 0.67)} viewBox="0 0 90 60" className="rounded-sm flex-shrink-0">
      <rect width="90" height="20" fill="#fff"/>
      <rect y="20" width="90" height="20" fill="#0039a6"/>
      <rect y="40" width="90" height="20" fill="#d52b1e"/>
    </svg>
  );
}

function FlagAE({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={Math.round(size * 0.5)} viewBox="0 0 120 60" className="rounded-sm flex-shrink-0">
      <rect width="120" height="20" fill="#00732f"/>
      <rect y="20" width="120" height="20" fill="#fff"/>
      <rect y="40" width="120" height="20" fill="#000"/>
      <rect width="30" height="60" fill="#ff0000"/>
    </svg>
  );
}

const FLAG_COMPONENTS: Record<string, React.FC<{ size?: number }>> = {
  he: FlagIL,
  en: FlagUS,
  ru: FlagRU,
  ar: FlagAE,
};

function FlagIcon({ langCode, size = 20 }: { langCode: string; size?: number }) {
  const Flag = FLAG_COMPONENTS[langCode];
  if (!Flag) return null;
  return <Flag size={size} />;
}

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (code: string) => {
    i18n.changeLanguage(code);
    const isRTL = RTL_LANGUAGES.includes(code);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
    setIsOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select language"
      >
        <FlagIcon langCode={i18n.language} size={22} />
        <span>{currentLang.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[170px] z-50"
          style={{ [RTL_LANGUAGES.includes(i18n.language) ? 'left' : 'right']: 0 }}
        >
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === i18n.language}
              onClick={() => handleChange(lang.code)}
              className={`w-full text-start px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                lang.code === i18n.language ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-gray-700'
              }`}
            >
              <FlagIcon langCode={lang.code} size={24} />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
